import React from 'react';
import { ScopeT } from 'business/definitions';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';
import { AlertFilter } from 'lib/alert/models';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { isAllExpression, isBlockExpression } from 'helpers/scopes';
import { isScopeFulfilled } from './helpers/protections';

const ScopeTooltip = ({
    scope,
    blockType,
}: {
    scope: ScopeT;
    blockType: string;
}): React.ReactElement | null => {
    const { t } = useTranslation();

    if (scope.expression === undefined) {
        return null;
    }

    if (isBlockExpression(scope.expression)) {
        return (
            <div>
                {t('alert.protection.one', { blockType })}{' '}
                {translateBlock(scope.expression.block)}
            </div>
        );
    }

    if (isAllExpression(scope.expression)) {
        return (
            <div>
                {t('alert.protection.all', { blockType })}
                <ul>
                    {scope.expression?.all?.map((b) => (
                        <li key={b.block}>{translateBlock(b.block)}</li>
                    ))}
                </ul>
            </div>
        );
    }

    return (
        <div>
            {t('alert.protection.any', { blockType })}
            <ul>
                {scope.expression?.any?.map((b) => (
                    <li key={b.block}>{translateBlock(b.block)}</li>
                ))}
            </ul>
        </div>
    );
};

const AlertScopeWarning = ({
    metricScope,
    dimensionScope,
    filters,
}: {
    metricScope: ScopeT | undefined;
    dimensionScope: ScopeT | undefined;
    filters: AlertFilter[];
}): React.ReactElement | null => {
    const { t } = useTranslation();
    if (metricScope === undefined && dimensionScope === undefined) {
        return null;
    }

    const metricFufilled =
        metricScope === undefined || isScopeFulfilled(metricScope, filters);
    const dimensionFufilled =
        dimensionScope === undefined ||
        isScopeFulfilled(dimensionScope, filters);
    if (metricFufilled && dimensionFufilled) {
        return null;
    }

    const Warning = (
        <Alert variant="outlined" severity="warning">
            <div>
                {!metricFufilled &&
                    t(
                        `alert.protection.${
                            metricScope.expression === undefined
                                ? 'restricted'
                                : 'protected'
                        }`,
                        {
                            blockType: t('alert.metric'),
                            scopeLabel: metricScope.label,
                        },
                    )}
            </div>
            <div>
                {!dimensionFufilled &&
                    t(
                        `alert.protection.${
                            dimensionScope.expression === undefined
                                ? 'restricted'
                                : 'protected'
                        }`,
                        {
                            blockType: t('alert.dimension'),
                            scopeLabel: dimensionScope.label,
                        },
                    )}
            </div>
        </Alert>
    );

    const metricRestricted =
        metricScope !== undefined && metricScope.expression === undefined;
    const dimensionRestricted =
        dimensionScope !== undefined && dimensionScope.expression === undefined;
    if (metricRestricted && dimensionRestricted) {
        return Warning;
    }

    return (
        <Tooltip
            title={
                <div>
                    {!metricRestricted && !metricFufilled && (
                        <ScopeTooltip
                            scope={metricScope}
                            blockType={t('alert.metric')}
                        />
                    )}
                    {!dimensionRestricted && !dimensionFufilled && (
                        <ScopeTooltip
                            scope={dimensionScope}
                            blockType={t('alert.dimension')}
                        />
                    )}
                </div>
            }
        >
            {Warning}
        </Tooltip>
    );
};

export default AlertScopeWarning;
